import React from "react";
import clsx from "clsx";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";

import {
  getImageBitmap,
  generateIphoneIconPreview,
  IphoneIconPreviewAssets,
} from "../../utils/canvas";

import frame from "../../assets/iphone-frame.jpeg";
import wallpaper from "../../assets/iphone-wallpaper.jpeg";
import phoneIcon from "../../assets/phone-appicon.png";
import safariIcon from "../../assets/safari-appicon.png";
import weatherIcon from "../../assets/weather-appicon.png";
import messagesIcon from "../../assets/messages-appicon.png";
import calculatorIcon from "../../assets/calculator-appicon.png";
import cameraIcon from "../../assets/camera-appicon.png";
import placeholderIcon from "../../assets/placeholder-appicon.png";

const PHONE_DISPLAY_RATIO = 1.4;
const CANVAS_WIDTH = 700;
const CANVAS_HEIGHT = CANVAS_WIDTH / PHONE_DISPLAY_RATIO;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: theme.spacing(50),
    borderColor: theme.palette.border.secondary,
    borderWidth: theme.spacing(0.5),
    borderRadius: theme.spacing(2),
    padding: theme.spacing(0, 1),
  },

  title: {
    marginTop: theme.spacing(2),
    color: theme.palette.border.primary,
  },

  canvasContainer: {
    width: "100%",
    aspectRatio: `${PHONE_DISPLAY_RATIO} / 1`,
  },

  canvas: {
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    userSelect: "none",
  },
}));

interface IphoneIconPreviewCanvasProps {
  className?: string;
  iconUrl: string;
  appName: string;
  previewTitle?: string;
}

export function IphoneIconPreviewCanvas(props: IphoneIconPreviewCanvasProps) {
  const { className, iconUrl, appName, previewTitle } = props;

  const s = useStyles();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [assetsLoaded, setAssetsLoaded] = React.useState(false);
  const assetsRef = React.useRef<Omit<
    IphoneIconPreviewAssets,
    "appIcon"
  > | null>(null);

  // Load static assets once
  React.useEffect(() => {
    const loadStaticAssets = async () => {
      try {
        const [
          frameImg,
          wallpaperImg,
          calculatorIconImg,
          weatherIconImg,
          phoneIconImg,
          messagesIconImg,
          safariIconImg,
          cameraIconImg,
        ] = await Promise.all([
          getImageBitmap(frame),
          getImageBitmap(wallpaper),
          getImageBitmap(calculatorIcon),
          getImageBitmap(weatherIcon),
          getImageBitmap(phoneIcon),
          getImageBitmap(messagesIcon),
          getImageBitmap(safariIcon),
          getImageBitmap(cameraIcon),
        ]);

        assetsRef.current = {
          frameImg,
          wallpaperImg,
          calculatorIcon: calculatorIconImg,
          weatherIcon: weatherIconImg,
          phoneIcon: phoneIconImg,
          messagesIcon: messagesIconImg,
          safariIcon: safariIconImg,
          cameraIcon: cameraIconImg,
        };
        setAssetsLoaded(true);
      } catch (error) {
        console.error("Failed to load iPhone preview assets:", error);
      }
    };

    loadStaticAssets();
  }, []);

  // Render preview when assets are loaded or when iconUrl/appName changes
  React.useEffect(() => {
    const renderPreview = async () => {
      if (!assetsLoaded || !assetsRef.current || !canvasRef.current) return;

      try {
        const appIconImg = await getImageBitmap(iconUrl || placeholderIcon);

        const assets: IphoneIconPreviewAssets = {
          ...assetsRef.current,
          appIcon: appIconImg,
        };

        await generateIphoneIconPreview(canvasRef, assets, appName || "My App");
      } catch (error) {
        console.error("Failed to render iPhone icon preview:", error);
      }
    };

    renderPreview();
  }, [assetsLoaded, iconUrl, appName]);

  return (
    <Box className={clsx(s.root, className)}>
      <Box className={s.canvasContainer}>
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className={s.canvas}
        />
      </Box>
      <Typography className={s.title} variant="h5" textAlign="center">
        {previewTitle || "App Icon Preview"}
      </Typography>
    </Box>
  );
}
